import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  ArrowLeft, Bold, Code2, Eye, GripVertical, Image as ImageIcon,
  LayoutTemplate, Monitor, Plus, Save, Smartphone, Trash2, Type,
  Upload, Loader2, Film, Images, ChevronLeft, ChevronRight, Tablet, X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useMarca, urlDeLogo } from '@/lib/marca';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ColorPalette from './ColorPalette';
import {
  BLOCK_PALETTE, MERGE_TAGS, applyPreviewVars, compileBlockPreview,
  compileDocument, defaultDocument, emptyBlock,
} from './compileDocument';

/**
 * S83.1 — Lienzo Bewe: clic en preview, drag reorder, toolbar texto, paleta colores.
 */
export default function EmailVisualEditor({ template, onBack, onSaved }) {
  const accent = '#F72585';
  const [form, setForm] = useState(() => ({
    key: template?.key || `campana-${Date.now().toString(36).slice(-6)}`,
    name: template?.name || 'Nueva campaña',
    subject: template?.subject || '{{tenant_name}} · un mensaje para ti',
    status: template?.status || 'draft',
    default_from_email: template?.default_from_email || '',
    default_from_name: template?.default_from_name || '',
  }));
  // S85 — si no hay design_json, abrir en Código con el HTML real (no el starter visual)
  const hasVisual = !!(template?.design_json && template.design_json.blocks?.length);
  const [doc, setDoc] = useState(() => (hasVisual ? template.design_json : defaultDocument(accent)));
  const [selectedId, setSelectedId] = useState(() => (hasVisual ? (template.design_json.blocks[0]?.id || null) : null));
  const [mode, setMode] = useState(() => (hasVisual ? 'visual' : 'code'));
  const [viewport, setViewport] = useState('desktop');
  const [htmlOverride, setHtmlOverride] = useState(template?.html_body || '');
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const textRef = useRef(null);
  const [templateId, setTemplateId] = useState(template?.id || null);
  const [loadError, setLoadError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile'); // mobile | tablet | desktop
  const { user } = useAuth();
  const marca = useMarca(user?.tenant_id);

  // Vars fijas de vista previa (no se editan en panel — el dueño ve el correo real)
  const previewVars = useMemo(() => {
    let logo = 'https://placehold.co/160x48/png?text=LOGO';
    if (user?.tenant_id && marca?.tiene_logo_ancho) {
      logo = urlDeLogo(user.tenant_id, 'wide', marca.sello_ancho);
    } else if (user?.tenant_id && marca?.tiene_logo_cuadrado) {
      logo = urlDeLogo(user.tenant_id, 'square', marca.sello_cuadrado);
    }
    return {
      nombre_cliente: 'María',
      tenant_name: marca?.nombre_negocio || 'Tu negocio',
      business_name: marca?.nombre_negocio || 'Tu negocio',
      logo_url: logo,
      enlace: 'https://ejemplo.com',
      fecha_cita: '15 ago · 11:00',
      contact_name: 'María',
      contact_email: 'maria@ejemplo.com',
      current_date: new Date().toLocaleDateString('es-MX'),
    };
  }, [user?.tenant_id, marca]);

  // S84 — siempre recargar del servidor al abrir (evita lista stale sin design_json)
  useEffect(() => {
    if (!template?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/tenant/email/templates/${template.id}`);
        if (cancelled) return;
        setTemplateId(data.id);
        setForm({
          key: data.key,
          name: data.name || '',
          subject: data.subject || '',
          status: data.status || 'draft',
          default_from_email: data.default_from_email || '',
          default_from_name: data.default_from_name || '',
        });
        if (data.design_json && data.design_json.blocks) {
          setDoc(data.design_json);
          setMode('visual');
          setSelectedId(data.design_json.blocks[0]?.id || null);
        } else if (data.html_body) {
          setHtmlOverride(data.html_body);
          setMode('code');
          toast.message('Esta plantilla no tiene diseño visual; abriendo en Código. Guarda en Visual para convertirlo.');
        }
        setDirty(false);
      } catch (e) {
        setLoadError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar la plantilla');
      }
    })();
    return () => { cancelled = true; };
  }, [template?.id]);

  const selected = useMemo(
    () => (doc.blocks || []).find(b => b.id === selectedId) || null,
    [doc, selectedId],
  );

  const updateBlock = useCallback((id, patch) => {
    setDoc(d => ({
      ...d,
      blocks: d.blocks.map(b => (b.id === id ? { ...b, ...patch } : b)),
    }));
    setDirty(true);
  }, []);

  const addBlock = (type) => {
    const block = emptyBlock(type, doc.meta?.accent || accent);
    setDoc(d => ({ ...d, blocks: [...(d.blocks || []), block] }));
    setSelectedId(block.id);
  };

  const removeBlock = (id) => {
    setDoc(d => ({ ...d, blocks: d.blocks.filter(b => b.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const reorder = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setDoc(d => {
      const blocks = [...d.blocks];
      const from = blocks.findIndex(b => b.id === fromId);
      const to = blocks.findIndex(b => b.id === toId);
      if (from < 0 || to < 0) return d;
      const [item] = blocks.splice(from, 1);
      blocks.splice(to, 0, item);
      return { ...d, blocks };
    });
  };

  const insertTag = (tag) => {
    const token = `{{${tag}}}`;
    if (!selected) {
      setForm(f => ({ ...f, subject: `${f.subject || ''}${token}` }));
      toast.message(`Variable en asunto: ${token}`);
      return;
    }
    if (selected.type === 'text') {
      const el = textRef.current;
      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const val = selected.html || '';
        const next = val.slice(0, start) + token + val.slice(end);
        updateBlock(selected.id, { html: next });
        requestAnimationFrame(() => {
          try { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } catch { /* */ }
        });
      } else {
        updateBlock(selected.id, { html: `${selected.html || ''}${token}` });
      }
    } else if (selected.type === 'hero') {
      updateBlock(selected.id, { greeting: `${selected.greeting || ''}${token}` });
    } else if (selected.type === 'button') {
      if (tag === 'enlace') updateBlock(selected.id, { url: token });
      else updateBlock(selected.id, { label: `${selected.label || ''}${token}` });
    } else if (selected.type === 'footer') {
      updateBlock(selected.id, { text: `${selected.text || ''}${token}` });
    } else {
      setForm(f => ({ ...f, subject: `${f.subject || ''}${token}` }));
    }
  };

  const wrapBold = () => {
    if (!selected || selected.type !== 'text') return;
    const el = textRef.current;
    const val = selected.html || '';
    if (el && el.selectionStart !== el.selectionEnd) {
      const a = el.selectionStart;
      const b = el.selectionEnd;
      const next = val.slice(0, a) + '<strong>' + val.slice(a, b) + '</strong>' + val.slice(b);
      updateBlock(selected.id, { html: next });
    } else {
      updateBlock(selected.id, { html: `${val}<strong>texto</strong>` });
    }
  };

const applyServerTemplate = (data) => {
    setTemplateId(data.id);
    setForm(f => ({
      ...f,
      key: data.key || f.key,
      name: data.name || f.name,
      subject: data.subject || f.subject,
      status: data.status || f.status,
      default_from_email: data.default_from_email || '',
      default_from_name: data.default_from_name || '',
    }));
    if (data.design_json && data.design_json.blocks) {
      setDoc(data.design_json);
      setMode('visual');
    } else if (data.html_body) {
      setHtmlOverride(data.html_body);
    }
    setDirty(false);
  };

  const handleSave = async (andClose = false) => {
    if (!form.name?.trim() || !form.subject?.trim()) {
      toast.error('Nombre y asunto son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        subject: form.subject,
        status: form.status,
        default_from_email: form.default_from_email || null,
        default_from_name: form.default_from_name || null,
      };
      if (mode === 'code') {
        payload.html_body = htmlOverride;
        payload.design_json = null;
        payload.clear_design = true;
      } else {
        payload.design_json = doc;
        payload.html_body = compileDocument(doc);
      }
      let data;
      if (templateId) {
        ({ data } = await api.put(`/tenant/email/templates/${templateId}`, payload));
      } else {
        payload.key = form.key || `campana-${Date.now().toString(36).slice(-6)}`;
        ({ data } = await api.post('/tenant/email/templates', payload));
      }
      // Revalidar desde servidor
      try {
        const fresh = await api.get(`/tenant/email/templates/${data.id}`);
        data = fresh.data;
      } catch { /* keep put response */ }
      applyServerTemplate(data);
      toast.success(andClose ? 'Plantilla guardada' : 'Guardado ✓ — puedes seguir editando');
      onSaved?.(data);
      if (andClose) onBack?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };


  const canvasWidth = viewport === 'mobile' ? 360 : viewport === 'tablet' ? 480 : 600;
  const blocks = doc.blocks || [];
  const isEmpty = blocks.length === 0;

  return (
    <div className="space-y-3" data-testid="email-visual-editor">
      {loadError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{loadError}</p>
      )}
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <button type="button" onClick={onBack}
          className="text-xs text-[#94A3B8] hover:text-[#0F172A] flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Volver a plantillas
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full border border-[#E2E8F0] overflow-hidden text-xs">
            <button type="button" onClick={() => setMode('visual')}
              className={`px-3 py-1.5 flex items-center gap-1 ${mode === 'visual' ? 'bg-[#0F172A] text-white' : 'text-[#64748B]'}`}
              data-testid="mode-visual">
              <LayoutTemplate className="w-3.5 h-3.5" /> Visual
            </button>
            <button type="button" onClick={() => {
              setHtmlOverride(compileDocument(doc));
              setMode('code');
            }}
              className={`px-3 py-1.5 flex items-center gap-1 ${mode === 'code' ? 'bg-[#0F172A] text-white' : 'text-[#64748B]'}`}
              data-testid="mode-code">
              <Code2 className="w-3.5 h-3.5" /> Código
            </button>
          </div>
          <div className="flex rounded-full border border-[#E2E8F0] overflow-hidden text-xs">
            <button type="button" onClick={() => setViewport('desktop')}
              className={`px-2.5 py-1.5 ${viewport === 'desktop' ? 'bg-[#F1F5F9] text-[#0F172A]' : 'text-[#94A3B8]'}`}
              aria-label="Escritorio" data-testid="viewport-desktop">
              <Monitor className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setViewport('tablet')}
              className={`px-2.5 py-1.5 ${viewport === 'tablet' ? 'bg-[#F1F5F9] text-[#0F172A]' : 'text-[#94A3B8]'}`}
              aria-label="Tablet" data-testid="viewport-tablet">
              <Tablet className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setViewport('mobile')}
              className={`px-2.5 py-1.5 ${viewport === 'mobile' ? 'bg-[#F1F5F9] text-[#0F172A]' : 'text-[#94A3B8]'}`}
              aria-label="Móvil" data-testid="viewport-mobile">
              <Smartphone className="w-3.5 h-3.5" />
            </button>
          </div>
          <Button type="button" variant="outline" className="text-xs rounded-full"
            onClick={() => { setPreviewDevice(viewport === 'desktop' ? 'desktop' : viewport === 'tablet' ? 'tablet' : 'mobile'); setPreviewOpen(true); }}
            data-testid="open-preview">
            <Eye className="w-3.5 h-3.5 mr-1" /> Vista previa
          </Button>
          {dirty && <span className="text-[10px] text-amber-600 font-medium" data-testid="unsaved-badge">Sin guardar</span>}
          <Button onClick={() => handleSave(false)} disabled={saving}
            className="text-xs rounded-full text-white" style={{ background: accent }}
            data-testid="visual-save">
            <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} variant="outline"
            className="text-xs rounded-full" data-testid="visual-save-close">
            Guardar y volver
          </Button>
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-white border border-[#E2E8F0] rounded-xl p-3">
        <div>
          <label className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wide">Nombre</label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="text-sm h-9" data-testid="visual-name" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wide">Asunto del correo</label>
          <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            className="text-sm h-9" data-testid="visual-subject" />
        </div>
        <div className="md:col-span-3 flex flex-wrap gap-1 items-center">
          <span className="text-[10px] text-[#94A3B8] mr-1">Variables:</span>
          {MERGE_TAGS.map(t => (
            <button key={t} type="button" onClick={() => insertTag(t)}
              className="text-[10px] font-mono bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-2 py-0.5 hover:border-[#F72585] hover:text-[#F72585]"
              data-testid={`merge-${t}`}>
              {`{{${t}}}`}
            </button>
          ))}
        </div>
      </div>

      {mode === 'code' ? (
        <div className="space-y-2" data-testid="code-mode">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Modo código (HTML del correo). Plantillas del sistema y las clásicas se editan aquí.
            Si pasas a Visual y guardas, se crea un diseño por bloques nuevo (el HTML viejo se reemplaza).
          </p>
          <textarea
            value={htmlOverride}
            onChange={e => setHtmlOverride(e.target.value)}
            rows={18}
            className="w-full text-xs font-mono border border-[#E2E8F0] rounded-xl p-3"
            data-testid="html-override"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 min-h-[520px]">
          {/* Palette */}
          <aside className="xl:col-span-2 bg-white border border-[#E2E8F0] rounded-xl p-3 space-y-2"
            data-testid="block-palette">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Bloques</p>
            {BLOCK_PALETTE.map(p => (
              <button key={p.type} type="button" onClick={() => addBlock(p.type)}
                className="w-full text-left rounded-lg border border-[#E2E8F0] hover:border-[#F72585]/30 hover:bg-[#FFF5F9] px-2.5 py-2 transition-colors"
                data-testid={`add-block-${p.type}`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F172A]">
                  <Plus className="w-3 h-3 text-[#F72585]" /> {p.label}
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">{p.hint}</p>
              </button>
            ))}
          </aside>

          {/* Canvas — clickable blocks */}
          <section className="xl:col-span-6 bg-[#E8EDF3] border border-[#E2E8F0] rounded-xl p-4 overflow-auto"
            data-testid="email-canvas">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold text-[#64748B] flex items-center gap-1">
                <Eye className="w-3 h-3" /> Clic en el correo para editar · arrastra ⋮⋮ para reordenar
              </p>
              <span className="text-[10px] text-[#94A3B8]">{canvasWidth}px</span>
            </div>

            {isEmpty ? (
              <EmptyCanvas
                onAddHero={() => addBlock('hero')}
                onAddText={() => addBlock('text')}
                onStarter={() => {
                  setDoc(defaultDocument(accent));
                  setSelectedId(null);
                }}
              />
            ) : (
              <div className="mx-auto transition-all" style={{ maxWidth: canvasWidth }} data-testid="live-preview">
                <div
                  className="bg-[#F1F5F9] rounded-xl overflow-hidden shadow-sm border border-[#E2E8F0]"
                  style={{ padding: viewport === 'mobile' ? '12px 8px' : '20px 12px' }}
                >
                  <div className="bg-white rounded-xl overflow-hidden border border-[#E2E8F0] mx-auto"
                    style={{ maxWidth: canvasWidth }}>
                    {blocks.map((b) => {
                      const selected = selectedId === b.id;
                      const isDragOver = dragOverId === b.id && dragId !== b.id;
                      const html = applyPreviewVars(compileBlockPreview(b, doc.meta), previewVars);
                      return (
                        <div
                          key={b.id}
                          draggable={b.type !== 'carousel'}
                          onDragStart={(e) => {
                            if (b.type === 'carousel') return;
                            setDragId(b.id);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', b.id);
                          }}
                          onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                          onDragOver={(e) => { e.preventDefault(); setDragOverId(b.id); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = e.dataTransfer.getData('text/plain') || dragId;
                            reorder(from, b.id);
                            setDragId(null);
                            setDragOverId(null);
                          }}
                          onClick={() => setSelectedId(b.id)}
                          className={`relative group cursor-pointer transition-shadow ${
                            selected ? 'ring-2 ring-[#F72585] ring-offset-1 z-10' : 'hover:ring-1 hover:ring-[#F72585]/40'
                          } ${isDragOver ? 'border-t-4 border-[#F72585]' : ''} ${dragId === b.id ? 'opacity-60' : ''}`}
                          data-testid={`canvas-block-${b.type}`}
                          data-block-id={b.id}
                        >
                          <div
                            className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 z-20 p-1 rounded bg-white/90 border border-[#E2E8F0] cursor-grab active:cursor-grabbing"
                            title="Arrastrar para reordenar"
                            data-testid={`drag-handle-${b.id}`}
                            draggable
                            onDragStart={(e) => {
                              e.stopPropagation();
                              setDragId(b.id);
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', b.id);
                            }}
                          >
                            <GripVertical className="w-3.5 h-3.5 text-[#64748B]" />
                          </div>
                          {selected && (
                            <div className="absolute right-1 top-1 z-20 flex gap-1">
                              <span className="text-[9px] font-bold uppercase bg-[#F72585] text-white px-1.5 py-0.5 rounded">
                                {blockLabel(b)}
                              </span>
                              <button type="button"
                                onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }}
                                className="p-0.5 rounded bg-white border border-red-200 text-red-500 hover:bg-red-50"
                                aria-label="Eliminar">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {b.type === 'carousel' ? (
                            <CarouselCanvas block={b} />
                          ) : (
                            <div
                              className="pointer-events-none"
                              dangerouslySetInnerHTML={{ __html: html }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* compact order list (secondary) */}
            {!isEmpty && (
              <div className="mt-3 flex flex-wrap gap-1" data-testid="block-chips">
                {blocks.map((b, i) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setSelectedId(b.id)}
                    className={`text-[10px] px-2 py-1 rounded-full border ${
                      selectedId === b.id
                        ? 'border-[#F72585] bg-[#FFF5F9] text-[#F72585] font-semibold'
                        : 'border-[#E2E8F0] bg-white text-[#64748B]'
                    }`}
                  >
                    {i + 1}. {blockLabel(b)}
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Properties */}
          <aside className="xl:col-span-4 bg-white border border-[#E2E8F0] rounded-xl p-3 space-y-3"
            data-testid="property-panel">
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Propiedades</p>
            {!selected && (
              <p className="text-xs text-[#94A3B8]">Haz clic en un bloque del correo o agrégalo desde la izquierda.</p>
            )}
            {selected && (
              <BlockProps
                block={selected}
                onChange={patch => updateBlock(selected.id, patch)}
                textRef={textRef}
                onBold={wrapBold}
                onInsertTag={insertTag}
              />
            )}
            <div className="pt-2 border-t border-[#E2E8F0]">
              <label className="text-[10px] text-[#94A3B8]">Estado</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full text-xs border border-[#E2E8F0] rounded-lg px-2 py-1.5 mt-0.5">
                <option value="draft">Borrador</option>
                <option value="active">Activa (lista para envíos)</option>
              </select>
            </div>
            <Button type="button" variant="outline" className="w-full text-xs"
              onClick={() => setPreviewOpen(true)} data-testid="props-open-preview">
              <Eye className="w-3.5 h-3.5 mr-1" /> Cómo lo ve el cliente
            </Button>
          </aside>
        </div>
      )}

      <p className="text-[11px] text-[#94A3B8] text-center">
        Compatible con Gmail, Outlook y Apple Mail · logo y dominio del tenant al enviar
      </p>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        device={previewDevice}
        onDevice={setPreviewDevice}
        html={applyPreviewVars(mode === 'code' ? htmlOverride : compileDocument(doc), previewVars)}
        subject={applyPreviewVars(form.subject || '', previewVars)}
      />
    </div>
  );
}


function CarouselCanvas({ block }) {
  const slides = (Array.isArray(block.slides) ? block.slides : [])
    .map((s, i) => (typeof s === 'string' ? { src: s, alt: `Imagen ${i + 1}` } : s))
    .filter(s => s?.src);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [slides.length, slides[0]?.src]);
  if (!slides.length) {
    return (
      <div className="bg-white px-6 py-10 text-center text-xs text-[#94A3B8]">
        [Carrusel: sube al menos una imagen]
      </div>
    );
  }
  const i = Math.min(idx, slides.length - 1);
  const s = slides[i];
  const go = (dir) => {
    setIdx(cur => {
      const n = cur + dir;
      if (n < 0) return slides.length - 1;
      if (n >= slides.length) return 0;
      return n;
    });
  };
  return (
    <div className="bg-white px-3 py-4 select-none" data-testid="carousel-canvas" onClick={e => e.stopPropagation()}>
      <div className="relative mx-auto max-w-full">
        <img
          src={s.src}
          alt={s.alt || `Imagen ${i + 1}`}
          className="w-full max-h-64 object-contain rounded-lg bg-[#F8FAFC]"
        />
        {slides.length > 1 && (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 border shadow flex items-center justify-center"
              data-testid="carousel-prev" aria-label="Anterior">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/95 border shadow flex items-center justify-center"
              data-testid="carousel-next" aria-label="Siguiente">
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2">
        {slides.map((_, j) => (
          <button key={j} type="button" onClick={(e) => { e.stopPropagation(); setIdx(j); }}
            className={`w-2 h-2 rounded-full ${j === i ? 'bg-[#F72585]' : 'bg-[#CBD5E1]'}`}
            aria-label={`Foto ${j + 1}`} />
        ))}
      </div>
      <p className="text-center text-[10px] text-[#94A3B8] mt-1">{i + 1} / {slides.length}</p>
    </div>
  );
}

function PreviewModal({ open, onClose, device, onDevice, html, subject }) {
  const widths = { mobile: 360, tablet: 520, desktop: 640 };
  const w = widths[device] || 360;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="email-preview-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <DialogHeader className="p-0">
            <DialogTitle className="text-sm font-semibold">Vista previa — así lo ve el cliente</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border overflow-hidden text-xs">
              {[
                ['mobile', Smartphone, 'Móvil'],
                ['tablet', Tablet, 'Tablet'],
                ['desktop', Monitor, 'PC'],
              ].map(([k, Icon, label]) => (
                <button key={k} type="button" onClick={() => onDevice(k)}
                  className={`px-2.5 py-1.5 flex items-center gap-1 ${device === k ? 'bg-[#0F172A] text-white' : 'text-[#64748B]'}`}
                  data-testid={`preview-device-${k}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" aria-label="Cerrar">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="px-4 py-2 bg-[#F8FAFC] border-b text-xs text-[#64748B]">
          <span className="font-medium text-[#0F172A]">Asunto: </span>{subject || '(sin asunto)'}
        </div>
        <div className="flex-1 overflow-auto bg-[#CBD5E1]/40 p-4 sm:p-6" data-testid="preview-frame-wrap">
          <div
            className={`mx-auto bg-white shadow-xl overflow-hidden transition-all ${
              device === 'mobile' ? 'rounded-[1.75rem] border-[10px] border-[#0F172A]' :
              device === 'tablet' ? 'rounded-2xl border-[8px] border-[#334155]' :
              'rounded-xl border border-[#E2E8F0]'
            }`}
            style={{ maxWidth: w, width: '100%' }}
            data-testid="preview-device-frame"
          >
            {device === 'mobile' && (
              <div className="h-5 bg-[#0F172A] flex items-center justify-center">
                <div className="w-16 h-1 rounded-full bg-white/30" />
              </div>
            )}
            <iframe
              title="Vista previa del correo"
              srcDoc={html}
              className="w-full border-0 bg-white"
              style={{ height: device === 'mobile' ? 560 : device === 'tablet' ? 640 : 720, minHeight: 400 }}
              sandbox="allow-same-origin allow-popups"
              data-testid="preview-iframe"
            />
          </div>
          <p className="text-center text-[11px] text-[#64748B] mt-3">
            Simulación de pantalla · el correo real usa el logo y datos de cada cliente
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ImageUploadField({ value, onUploaded, testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo imágenes (JPG, PNG, WEBP, GIF)');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Máximo 8 MB por imagen');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/tenant/email/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.public_url || data.public_path;
      if (!url) throw new Error('Sin URL');
      onUploaded(url);
      toast.success('Imagen guardada en tu espacio');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo subir la imagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={testId ? `${testId}-input` : 'image-upload-input'}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        className="w-full text-xs h-9 border-dashed border-[#F72585]/40 text-[#0F172A]"
        onClick={() => inputRef.current?.click()}
        data-testid={testId ? `${testId}-btn` : 'image-upload-btn'}
      >
        {uploading ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Subiendo…</>
        ) : (
          <><Upload className="w-3.5 h-3.5 mr-1.5" /> Subir imagen</>
        )}
      </Button>
      {value ? (
        <p className="text-[10px] text-emerald-700 truncate">✓ Imagen lista</p>
      ) : (
        <p className="text-[10px] text-[#94A3B8]">Se guarda en el almacenamiento de tu negocio (R2 / disco).</p>
      )}
    </div>
  );
}

function EmptyCanvas({ onAddHero, onAddText, onStarter }) {

  return (
    <div
      className="rounded-xl border-2 border-dashed border-[#CBD5E1] bg-white/60 py-12 px-6 text-center space-y-4"
      data-testid="editor-empty"
    >
      <p className="text-sm font-semibold text-[#0F172A]">Tu correo está vacío</p>
      <p className="text-xs text-[#64748B] max-w-sm mx-auto">
        Empieza con un encabezado y un texto, o carga el starter con logo, CTA y pie legal.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button type="button" onClick={onStarter} className="text-xs rounded-full text-white bg-[#F72585]"
          data-testid="empty-starter">
          Usar diseño base
        </Button>
        <Button type="button" variant="outline" onClick={onAddHero} className="text-xs rounded-full"
          data-testid="empty-add-hero">
          + Encabezado
        </Button>
        <Button type="button" variant="outline" onClick={onAddText} className="text-xs rounded-full"
          data-testid="empty-add-text">
          + Texto
        </Button>
      </div>
    </div>
  );
}

function blockLabel(b) {
  const map = {
    logo: 'Logo', hero: 'Encabezado', text: 'Texto', image: 'Imagen',
    video: 'Video', carousel: 'Carrusel',
    button: 'Botón', spacer: 'Espacio', divider: 'Divisor', footer: 'Pie',
  };
  return map[b.type] || b.type;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-[#64748B] block mb-0.5">{label}</label>
      {children}
    </div>
  );
}

function BlockProps({ block, onChange, textRef, onBold, onInsertTag }) {
  const set = (k, v) => onChange({ [k]: v });

  if (block.type === 'logo') {
    const source = block.source === 'custom' ? 'custom' : 'branding';
    return (
      <div className="space-y-2" data-testid="prop-logo">
        <p className="text-[11px] text-[#64748B]">¿Qué logo mostrar?</p>
        <div className="grid grid-cols-2 gap-1.5">
          <button type="button"
            onClick={() => onChange({ source: 'branding', src: '' })}
            className={`text-[11px] font-semibold rounded-xl py-2 border ${
              source === 'branding' ? 'border-[#F72585] bg-[#FFF5F9] text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B]'
            }`}
            data-testid="logo-use-branding">
            Logo del negocio
          </button>
          <button type="button"
            onClick={() => onChange({ source: 'custom' })}
            className={`text-[11px] font-semibold rounded-xl py-2 border ${
              source === 'custom' ? 'border-[#F72585] bg-[#FFF5F9] text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B]'
            }`}
            data-testid="logo-use-custom">
            Subir otro
          </button>
        </div>
        {source === 'branding' ? (
          <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg p-2">
            Se usa el logo de tu marca (el que tienes en branding / R2) al enviar.
          </p>
        ) : (
          <ImageUploadField
            value={block.src || ''}
            onUploaded={(url) => onChange({ source: 'custom', src: url })}
            testId="logo-upload"
          />
        )}
        <ColorPalette label="Color de fondo" value={block.bg || '#FFFFFF'} onChange={v => set('bg', v)} testId="logo-bg" />
        <Field label="Alto máximo (px)">
          <Input type="number" className="h-8 text-xs" value={block.maxHeight || 56}
            onChange={e => set('maxHeight', Number(e.target.value) || 56)} />
        </Field>
        <Field label="Alineación">
          <select className="w-full text-xs border rounded-lg px-2 py-1.5" value={block.align || 'center'}
            onChange={e => set('align', e.target.value)}>
            <option value="left">Izquierda</option>
            <option value="center">Centro</option>
            <option value="right">Derecha</option>
          </select>
        </Field>
      </div>
    );
  }

  if (block.type === 'hero') {
    return (
      <div className="space-y-2">
        <Field label="Saludo">
          <Input className="h-8 text-xs" value={block.greeting || ''} onChange={e => set('greeting', e.target.value)} />
        </Field>
        <Field label="Título">
          <Input className="h-8 text-xs" value={block.title || ''} onChange={e => set('title', e.target.value)} data-testid="prop-hero-title" />
        </Field>
        <Field label="Título acento">
          <Input className="h-8 text-xs" value={block.titleAccent || ''} onChange={e => set('titleAccent', e.target.value)} />
        </Field>
        <ColorPalette label="Fondo del encabezado" value={block.bg || '#0F172A'} onChange={v => set('bg', v)} testId="hero-bg" />
        <ColorPalette label="Color acento" value={block.accentColor || '#F72585'} onChange={v => set('accentColor', v)} testId="hero-accent" />
      </div>
    );
  }

  if (block.type === 'text') {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1 pb-1" data-testid="text-toolbar">
          <button type="button" onClick={onBold}
            className="text-[11px] px-2 py-1 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] flex items-center gap-1"
            data-testid="toolbar-bold">
            <Bold className="w-3 h-3" /> Negrita
          </button>
          <button type="button" onClick={() => onInsertTag?.('nombre_cliente')}
            className="text-[11px] px-2 py-1 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC]">
            + nombre
          </button>
          <button type="button" onClick={() => onInsertTag?.('enlace')}
            className="text-[11px] px-2 py-1 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC]">
            + enlace
          </button>
        </div>
        <Field label="Contenido">
          <textarea
            ref={textRef}
            className="w-full text-xs border rounded-lg p-2 min-h-[120px]"
            value={block.html || ''}
            onChange={e => set('html', e.target.value)}
            data-testid="prop-text-html"
          />
        </Field>
        <ColorPalette label="Color del texto" value={block.color || '#334155'} onChange={v => set('color', v)} testId="text-color" />
        <Field label="Tamaño">
          <Input type="number" className="h-8 text-xs" value={block.size || 16}
            onChange={e => set('size', Number(e.target.value) || 16)} />
        </Field>
      </div>
    );
  }

  if (block.type === 'image') {
    return (
      <div className="space-y-2">
        <ImageUploadField
          value={block.src || ''}
          onUploaded={(url) => set('src', url)}
          testId="prop-image-upload"
        />
        <Field label="O pega una URL (opcional)">
          <Input className="h-8 text-xs" value={block.src || ''} onChange={e => set('src', e.target.value)}
            placeholder="https://…" data-testid="prop-image-src" />
        </Field>
        {block.src ? (
          <img src={block.src} alt="" className="w-full max-h-28 object-contain rounded-lg border bg-[#F8FAFC]" />
        ) : null}
        <Field label="Texto alternativo">
          <Input className="h-8 text-xs" value={block.alt || ''} onChange={e => set('alt', e.target.value)} />
        </Field>
        <Field label="Ancho (px)">
          <Input type="number" className="h-8 text-xs" value={block.width || 520}
            onChange={e => set('width', Number(e.target.value) || 520)} />
        </Field>
      </div>
    );
  }

  if (block.type === 'video') {
    return (
      <div className="space-y-2" data-testid="prop-video">
        <Field label="Enlace de YouTube">
          <Input className="h-8 text-xs" value={block.url || ''} onChange={e => set('url', e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…" data-testid="prop-video-url" />
        </Field>
        <Field label="Texto del botón / título">
          <Input className="h-8 text-xs" value={block.title || ''} onChange={e => set('title', e.target.value)}
            placeholder="Ver video" />
        </Field>
        <p className="text-[11px] text-[#64748B] flex items-start gap-1">
          <Film className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          En el correo se muestra la miniatura oficial de YouTube; al tocarla abre el video.
        </p>
      </div>
    );
  }

  if (block.type === 'carousel') {
    const slides = Array.isArray(block.slides) ? block.slides : [];
    const updateSlide = (idx, patch) => {
      const next = slides.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      set('slides', next);
    };
    const addSlide = () => set('slides', [...slides, { src: '', alt: `Imagen ${slides.length + 1}`, link: '' }]);
    const removeSlide = (idx) => set('slides', slides.filter((_, i) => i !== idx));
    return (
      <div className="space-y-3" data-testid="prop-carousel">
        <p className="text-[11px] text-[#64748B] flex items-start gap-1">
          <Images className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Sube varias fotos. En el editor las recorres con flechas ← →; en el correo salen en fila horizontal.
        </p>
        {slides.map((s, idx) => (
          <div key={idx} className="rounded-xl border border-[#E2E8F0] p-2 space-y-1.5 bg-[#F8FAFC]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-[#64748B]">Foto {idx + 1}</span>
              {slides.length > 1 && (
                <button type="button" className="text-[10px] text-red-500" onClick={() => removeSlide(idx)}>Quitar</button>
              )}
            </div>
            <ImageUploadField
              value={s.src || ''}
              onUploaded={(url) => updateSlide(idx, { src: url })}
              testId={`carousel-upload-${idx}`}
            />
            <Input className="h-7 text-[11px]" value={s.src || ''} placeholder="URL"
              onChange={e => updateSlide(idx, { src: e.target.value })} />
            <Input className="h-7 text-[11px]" value={s.alt || ''} placeholder="Texto alternativo"
              onChange={e => updateSlide(idx, { alt: e.target.value })} />
            <Input className="h-7 text-[11px]" value={s.link || ''} placeholder="Enlace al hacer clic (opcional)"
              onChange={e => updateSlide(idx, { link: e.target.value })} />
          </div>
        ))}
        {slides.length < 8 && (
          <Button type="button" variant="outline" size="sm" className="w-full text-xs" onClick={addSlide}
            data-testid="carousel-add-slide">
            <Plus className="w-3 h-3 mr-1" /> Agregar foto
          </Button>
        )}
      </div>
    );
  }

  if (block.type === 'button') {
    return (
      <div className="space-y-2">
        <Field label="Texto del botón">
          <Input className="h-8 text-xs" value={block.label || ''} onChange={e => set('label', e.target.value)} data-testid="prop-btn-label" />
        </Field>
        <Field label="Enlace">
          <Input className="h-8 text-xs" value={block.url || ''} onChange={e => set('url', e.target.value)} data-testid="prop-btn-url" />
        </Field>
        <ColorPalette label="Color del botón" value={block.bg || '#F72585'} onChange={v => set('bg', v)} testId="btn-bg" />
        <ColorPalette label="Color del texto" value={block.color || '#FFFFFF'} onChange={v => set('color', v)} testId="btn-color" />
        <Field label="Bordes redondeados">
          <Input type="number" className="h-8 text-xs" value={block.radius || 10}
            onChange={e => set('radius', Number(e.target.value) || 0)} />
        </Field>
      </div>
    );
  }

  if (block.type === 'spacer') {
    return (
      <Field label="Alto (px)">
        <Input type="number" className="h-8 text-xs" value={block.height || 16}
          onChange={e => set('height', Number(e.target.value) || 16)} />
      </Field>
    );
  }

  if (block.type === 'divider') {
    return (
      <ColorPalette label="Color de línea" value={block.color || '#E2E8F0'} onChange={v => set('color', v)} testId="divider-color" />
    );
  }

  if (block.type === 'footer') {
    return (
      <Field label="Texto legal">
        <textarea className="w-full text-xs border rounded-lg p-2 min-h-[100px]" value={block.text || ''}
          onChange={e => set('text', e.target.value)} />
      </Field>
    );
  }

  return <p className="text-xs text-[#94A3B8] flex items-center gap-1"><Type className="w-3 h-3" /> Sin propiedades</p>;
}
